// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@awc/App/Components/Notify";
import SubmitContactUsForm from "@awc/App/Handlers/CustomForms/ContactUsFormHandler";
import { MainRfqApiParameter } from "@awc/App/Services/CustomForms";
import { xIsAuthenticated } from "@awc/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@awc/App/Store/Selectors/SettingsSelectors";
import { xGoogleAnalyticsState } from "@awc/App/Widgets/GoogleAnalyticsConnector/Store/Reducers";
import { trackContactUsFormGa4Event } from "@awc/App/Widgets/GoogleAnalyticsConnector/Utilities/DataLayer";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { applyMask, getUtmParams, mergeWithCss, useValidate, xDefaultRules } from "@awc/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadBillTo from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/LoadBillTo";
import translate from "@insite/client-framework/Translate";
import { CountryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import FileUpload from "@insite/mobius/FileUpload";
import Select from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC, RefObject, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { AnyStyledComponent } from "styled-components";

interface OwnProps {
    extendedStyles?: ContactUsFormSyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    countries: getCurrentCountries(state),
    currentBillTo: getCurrentBillToState(state).value,
    session: state.context.session,
    reCaptchaEnabledForContactUs:
        getSettingsCollection(state).xngSecuritySettings.checkReCaptchaForCustomContactUsFormOnClientSide,
    thankYouUrl: getPageLinkByPageType(state, "RfqSubmitThankYou")?.url,
    isAuth: xIsAuthenticated(state),
    contactUsEvent: (state as xGoogleAnalyticsState).xGoogleAnalytics.contactUsEvent,
});

const mapDispatchToProps = {
    SubmitContactUsForm,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
    loadCurrentCountries,
    loadBillTo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface ContactUsFormSyles {
    formWrapper?: InjectableCss;
    inputsWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    fileUpload?: InjectableCss;
}

export const contactUsFormStyles: ContactUsFormSyles = {};

const StyledForm = getStyledWrapper("form");

const ContactUsForm: FC<Props> = ({
    SubmitContactUsForm,
    loadCurrentCountries,
    loadBillTo,
    countries,
    reCaptchaEnabledForContactUs,
    extendedStyles,
    session,
    currentBillTo,
    thankYouUrl,
    isAuth,
    validateReCaptcha,
    contactUsEvent,
}) => {
    const [styles] = useState(() => mergeWithCss(contactUsFormStyles, extendedStyles));
    const [errorMessage, setErrorMessage] = useState("");
    const [country, setCountry] = useState<CountryModel>();
    const [file, setFile] = useState<any>(null);

    const history = useHistory();

    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();

    const { masksList, setFieldValue, values, field, handleSubmit, isSubmitting, errors } = useValidate({
        initialValues: {
            FirstName: session.firstName || "",
            LastName: session.lastName || "",
            Email: session.email || "",
            Company: "",
            Phone: "",
            CountryCode: "",
            City: "",
            State: "",
            PostalCode: "",
            Notes: "",
            AttachmentBase64: "",
            AttachmentFileName: "",
            AttachmentMimeType: "",
        },
        validators: {
            FirstName: `required|${xDefaultRules.firstName}`,
            LastName: `required|${xDefaultRules.lastName}`,
            Email: `required|${xDefaultRules.email}`,
            Company: `required|${xDefaultRules.companyName}`,
            Phone: `required|${xDefaultRules.phone}`,
            CountryCode: `required|${xDefaultRules.city}`,
            City: `required|${xDefaultRules.city}`,
            State: country?.states?.length ? `required|${xDefaultRules.text}` : `${xDefaultRules.text}`,
            PostalCode: `${
                country?.abbreviation?.toLowerCase() === "ca"
                    ? "postalCodeCA"
                    : country?.abbreviation?.toLowerCase() === "us"
                      ? "postalCodeUS"
                      : "text"
            }`,
            Notes: `required|${xDefaultRules.notes}`,
        },
        onSubmit: (fieldValues: MainRfqApiParameter, { setSubmitting }: any) => {
            submitHandler(fieldValues, setSubmitting);
        },
    });

    // Load countries if not available and set selected country
    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        } else if (isAuth && currentBillTo) {
            // Set Selected Country
            const selectedCountry = countries?.find(c => c.abbreviation === currentBillTo?.country?.abbreviation);
            setCountry(selectedCountry);
            selectedCountry && setFieldValue("CountryCode", selectedCountry?.abbreviation);

            // Set Selected State
            const selectedState = selectedCountry?.states?.find(
                c => c.abbreviation === currentBillTo?.state?.abbreviation,
            );
            selectedState && setFieldValue("State", selectedState?.abbreviation);
        }
    }, [countries, currentBillTo]);

    // Load billing info if missing and set form fields
    useEffect(() => {
        if (!isAuth) {
            return;
        }

        if (!currentBillTo) {
            loadBillTo();
        } else {
            setFieldValue("Company", currentBillTo.companyName);
            setFieldValue("Phone", currentBillTo.phone);
            setFieldValue("City", currentBillTo.city);
            setFieldValue("PostalCode", currentBillTo.postalCode);
        }
    }, [currentBillTo]);

    const submitHandler = async (fieldValues: MainRfqApiParameter, setSubmitting: any) => {
        setSubmitting(true);

        if (Object.keys(errors).length > 0) {
            setSubmitting(false);
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CustomContactUsForm", recaptchaRef });

        if (!isReCaptchaValid) {
            setSubmitting(false);
            return;
        }

        const utmParameters = getUtmParams();

        SubmitContactUsForm({
            ...fieldValues,
            ...(utmParameters ? { utmParameters, formUrl: window.location.href } : {}),
            onSuccess: () => {
                setSubmitting(false);
                setErrorMessage("");
                contactUsEvent && trackContactUsFormGa4Event();
                thankYouUrl && history.push(thankYouUrl);
            },
            onError: (error: string) => {
                setSubmitting(false);
                setErrorMessage(error);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const handleUploadFileChange = (e: ChangeEvent<AnyStyledComponent>) => {
        const reader = new FileReader();
        const attachmentFile = e.target.files?.[0];
        setFile(attachmentFile);
        reader.onload = () => {
            const base64DataUrl = reader.result as string;
            const base64Data = base64DataUrl?.split(",")[1];

            setFieldValue("AttachmentBase64", base64Data);
            setFieldValue("AttachmentFileName", attachmentFile.name);
            setFieldValue("AttachmentMimeType", attachmentFile.type);
        };
        reader.readAsDataURL(attachmentFile);
    };

    return (
        <StyledForm onSubmit={handleSubmit} noValidate data-test-selector="contactUsFrom">
            <StyledWrapper {...styles.formWrapper}>
                <StyledWrapper {...styles.inputsWrapper}>
                    <TextField
                        {...field("FirstName")}
                        label={translate("First name")}
                        required={true}
                        {...{ displayStarForRequired: true }}
                        error={field("FirstName").error && "First name is a required field"}
                    />

                    <TextField
                        {...field("LastName")}
                        label={translate("Last Name")}
                        required={true}
                        {...{ displayStarForRequired: true }}
                        error={field("LastName").error && "Last name is a required field"}
                    />

                    <TextField
                        {...field("Email")}
                        type="email"
                        label={translate("Email")}
                        required={true}
                        {...{ displayStarForRequired: true }}
                        error={field("Email").error && ""}
                    />

                    <TextField
                        {...field("Company")}
                        label={translate("Company name")}
                        error={field("Company").error && "Company name is a required field"}
                        required={true}
                        {...{ displayStarForRequired: true }}
                    />

                    <TextField
                        {...field("Phone")}
                        type="phone"
                        label={translate("Phone number")}
                        error={field("Phone").error && "Phone number is a required field"}
                        required={true}
                        {...{ displayStarForRequired: true }}
                    />

                    {countries && countries.length > 1 && (
                        <Select
                            {...field("CountryCode")}
                            label={translate("Country")}
                            onChange={(e: any) => {
                                const key = e.target.name;
                                const oldValue = values[key];
                                const newValue = e.target.value;
                                const masked = applyMask(masksList[key], newValue, oldValue);
                                setFieldValue(key, masked);
                                // XNG-Changes
                                const selectedCountry = countries?.find(c => c.abbreviation === masked);
                                setCountry(selectedCountry);
                                if (!selectedCountry?.states?.length) {
                                    setFieldValue("State", "");
                                } else if (selectedCountry.states.length === 1) {
                                    setFieldValue("State", selectedCountry.states[0].abbreviation);
                                }
                            }}
                            error={field("CountryCode").error && "Country is a required field"}
                            required={true}
                            {...{ displayStarForRequired: true }}
                        >
                            <option value="">{translate("Select Country")}</option>
                            {countries?.map((o: any) => (
                                <option key={o.abbreviation} value={o.abbreviation}>
                                    {o.name}
                                </option>
                            ))}
                        </Select>
                    )}

                    {country && (
                        <TextField
                            {...field("City")}
                            label={translate("City")}
                            required={true}
                            {...{ displayStarForRequired: true }}
                            error={field("City").error && "City is a required field"}
                        />
                    )}

                    {country && (
                        <TextField
                            {...field("PostalCode")}
                            label={translate("Postal Code")}
                            type={
                                country?.abbreviation?.toLowerCase() === "ca"
                                    ? "postalCodeCA"
                                    : country?.abbreviation?.toLowerCase() === "us"
                                      ? "postalCodeUS"
                                      : "text"
                            }
                            error={field("PostalCode").error && "Postal code is a required field"}
                        />
                    )}

                    {country && country?.states && !!country.states?.length && (
                        <Select
                            {...field("State")}
                            label={translate("State/Region")}
                            error={field("State").error && "State/Region is a required field"}
                            required={true}
                            {...{ displayStarForRequired: true }}
                        >
                            <option value="" disabled>
                                {translate("Select State")}
                            </option>
                            {country.states.map((o: any) => (
                                <option key={o.abbreviation} value={o.abbreviation}>
                                    {o.name}
                                </option>
                            ))}
                        </Select>
                    )}
                </StyledWrapper>

                <TextArea
                    label={translate("Message")}
                    placeholder={siteMessage("Tell us what you are looking for!") as string}
                    {...field("Notes")}
                    onChange={e => setFieldValue("Notes", e.target.value)}
                    error={field("Notes").error && "Message is a required field"}
                    required={true}
                    {...{ showRequiredStar: true }}
                />

                <StyledWrapper {...styles.fileUpload}>
                    <FileUpload
                        onFileChange={e => {
                            handleUploadFileChange(e);
                        }}
                        inputRef={fileUploadRef}
                        fileName={file?.name || ""}
                        labelPosition="top"
                        uid="contactUs"
                        buttonText={translate("Choose File")}
                        label={translate("Upload Attachment")}
                        buttonProps={{
                            variant: "tertiary",
                        }}
                    />
                </StyledWrapper>

                <ReCaptcha
                    recaptchaRef={recaptchaRef}
                    location="CustomContactUsForm"
                    {...{ forceEnable: reCaptchaEnabledForContactUs }}
                />
                <Button
                    type="submit"
                    disabled={isSubmitting}
                    {...styles.submitButton}
                    data-test-selector="contactUs_submit_button"
                >
                    {translate("Submit")}
                </Button>
                {errorMessage && (
                    <Notify type="danger" data-test-selector="contactUsFrom_ErrorMessage">
                        {errorMessage}
                    </Notify>
                )}
            </StyledWrapper>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ContactUsForm),
    definition: {
        displayName: "Contact Us From",
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [],
    },
};

export default widgetModule;
